import type { ReactNode } from 'react';
import { motion } from 'motion/react';

import { Wordmark } from '../layout/Wordmark.js';
import { Stamp } from '../ui/Stamp.js';

interface AuthLayoutProps {
  title: string;
  subtitle?: string;
  badge?: string;
  children: ReactNode;
  /** Bloco lateral (right column em wide viewport) — opcional. */
  side?: ReactNode;
}

/**
 * Layout dedicado para telas de auth (login, register, recovery).
 *
 * Não usa o PageShell principal — auth é momento dedicado, sem distrações
 * de header/footer da app autenticada. Mantém a paleta papel + identidade
 * (wordmark, stamp, layout asymmetric).
 */
export function AuthLayout({ title, subtitle, badge, children, side }: AuthLayoutProps) {
  return (
    <div className="min-h-screen bg-[var(--bg-paper)]">
      <header className="border-b border-[var(--border-subtle)]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-5">
          <a href="#/" className="inline-flex">
            <Wordmark size="md" />
          </a>
          <Stamp>v0.2.0 · MVP</Stamp>
        </div>
      </header>

      <main className="mx-auto grid max-w-5xl gap-10 px-5 py-10 sm:px-6 sm:py-12 lg:grid-cols-[1fr_320px] lg:gap-20 lg:py-20">
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeOut' }}
        >
          {badge && (
            <p className="mb-4 inline-block">
              <Stamp>{badge}</Stamp>
            </p>
          )}
          <h1 className="font-serif text-[32px] font-medium leading-[1.05] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px] lg:text-[44px]">
            {title}
            <span className="text-[var(--accent-saffron)]">.</span>
          </h1>
          {subtitle && (
            <p className="mt-4 max-w-md font-sans text-[15px] leading-relaxed text-[var(--ink-secondary)] sm:text-base">
              {subtitle}
            </p>
          )}
          <div className="mt-8 sm:mt-10">{children}</div>
        </motion.section>

        {side && (
          <aside className="border-t border-[var(--border-subtle)] pt-8 lg:border-t-0 lg:pt-0">
            {side}
          </aside>
        )}
      </main>

      <footer className="border-t border-[var(--border-subtle)] bg-[var(--bg-paper-elevated)]">
        <div className="mx-auto max-w-5xl px-6 py-6 font-mono text-[11px] text-[var(--ink-tertiary)]">
          SmartApply · BYOK · sua API key fica só em sessão. Senha em Argon2id, sessões revogáveis.
        </div>
      </footer>
    </div>
  );
}
